<script setup lang="ts">
import type { StockResult, StockResultStock } from '~/types/Stock'

// Resolves the single line being edited from the live draft, so the promise updates before Update
// is pressed. The Order Summary's CartLeadTime aggregates the whole basket instead.
const props = defineProps<{ productStoreLocation: ProductStoreLocation }>()

const productStore = useProductStore(props.productStoreLocation)
const basketsStore = useBasketsStore()
const checkoutStore = useCheckoutStore()
const shopStore = useShopStore()
const { t } = useI18n()
const { bopisEnabled } = useFeatureFlags()
const { resolveCartLine } = useCartCollectScenario()
const { buildCollectRow } = useCollectRow()
const { getStoreStock } = useStoreStock()

const showCollect = computed(() => bopisEnabled() && checkoutStore.isClickAndCollect)

// The SKU this draft would save as — updateItem() sends the same value.
const draftSku = computed(
  () => productStore.selectedProcessedSizeOptions?.sfccVariantSKU || productStore.selectedProduct?.id,
)

// Mirrors ProductAvailability's non-size special-order detection.
const isSpecialOrderDraft = computed(() => {
  if (productStore.selectedProcessedSizeOptions) return !!productStore.selectedProcessedSizeOptions.isSpecialOrder
  const hasInStockOption = productStore.selectedProduct?.c_mhj_processedSizeOptions?.some(
    (option) => option.inStock && !option.isSpecialOrder,
  )
  return !!productStore.selectedProduct?.c_mhj_isSpecialOrderProduct && !hasInStockOption
})

// A size sold as its own SKU saves with that variant's core size, so only a size made from the
// edited SKU is a resize.
const draftCoreSize = computed(() => {
  const option = productStore.selectedProcessedSizeOptions
  const product = productStore.selectedProduct
  return option?.sfccVariantSKU && option.sfccVariantSKU !== product?.id ? option.size : product?.c_coreSize
})

const draftFacts = computed(() => ({
  availableForClickAndCollect: !!productStore.selectedProduct?.c_availableForClickAndCollect,
  engravingText: productStore.engravingText,
  isSpecialOrder: isSpecialOrderDraft.value,
  // Mirrors the tile's isResizedForYouProduct.
  isResized:
    !!productStore.selectedRingSize &&
    productStore.selectedRingSize !== draftCoreSize.value &&
    !isSpecialOrderDraft.value,
}))

const storefrontInStock = computed(() =>
  productStore.selectedProcessedSizeOptions
    ? !!productStore.selectedProcessedSizeOptions.inStock
    : !!(productStore.productAvailabilityModel?.isInStock || productStore.productAvailabilityModel?.isLowStock),
)

const stockLineFor = (stock: StockResult | undefined, sku?: string, storeId?: string): StockResultStock | undefined => {
  const store = storeId ? stock?.stores?.find((entry) => entry.store === storeId) : stock?.stores?.[0]
  return store?.stockLevels?.find((line) => line.sku === sku)
}

// Keyed by store and SKU so a superseded draft's response can't be read as the current one's.
// null records a completed lookup that returned no line for the SKU.
const draftStockByKey = ref<Record<string, StockResultStock | null>>({})
const pendingStockKeys = new Set<string>()
const stockKey = (storeId?: string, sku?: string) => (storeId && sku ? `${storeId}:${sku}` : undefined)

// The bag-wide query only covers saved SKUs, so a draft size change needs its own lookup.
// undefined while pending or after a failure; null once completed without a line.
const draftStockLine = computed<StockResultStock | null | undefined>(() => {
  const fromBasketQuery = stockLineFor(checkoutStore.stock, draftSku.value, shopStore.myStoreId)
  if (fromBasketQuery) return fromBasketQuery
  const key = stockKey(shopStore.myStoreId, draftSku.value)
  return key ? draftStockByKey.value[key] : undefined
})

watch(
  [draftSku, () => shopStore.myStoreId, showCollect],
  async ([sku, storeId, collecting]) => {
    if (!collecting || !sku || !storeId || stockLineFor(checkoutStore.stock, sku, storeId)) return
    const key = stockKey(storeId, sku)
    if (!key || draftStockByKey.value[key] !== undefined || pendingStockKeys.has(key)) return
    pendingStockKeys.add(key)
    try {
      // `result` survives a failed lookup, so read the request's own response instead.
      const response = await getStoreStock([storeId], [sku])
      // A failed lookup isn't recorded, so revisiting the SKU retries it.
      if (response) draftStockByKey.value[key] = stockLineFor(response, sku, storeId) ?? null
    } finally {
      pendingStockKeys.delete(key)
    }
  },
  { immediate: true },
)

// Overlaying the draft at the line's existing basket position keeps the allocation order saving
// would produce.
const draftBasket = computed(() => {
  const basket = basketsStore.basket
  const itemId = productStore.selectedBasketItemId
  if (!basket?.productItems || !itemId) return basket
  return {
    ...basket,
    productItems: basket.productItems.map((item) =>
      item.itemId === itemId
        ? {
            ...item,
            productId: draftSku.value,
            c_engravingText: productStore.engravingText,
            c_specialOrderSKU: isSpecialOrderDraft.value ? (item.c_specialOrderSKU ?? draftSku.value) : undefined,
            c_ringSize: productStore.selectedRingSize,
            productCustomAttributes: {
              ...item.productCustomAttributes,
              c_coreSize: draftCoreSize.value,
              c_availableForClickAndCollect: !!productStore.selectedProduct?.c_availableForClickAndCollect,
            },
          }
        : item,
    ),
  }
})

const stockConsumingIds = computed(() => stockConsumingLineIds(draftBasket.value, draftSku.value))

const collectRow = computed(() => {
  if (!showCollect.value || !productStore.selectedProduct) return null
  if (!shopStore.myStore) return null
  // Hold back until the line's lookup completes, so the callout can't flash a wrong promise.
  if (!checkoutStore.stock || draftStockLine.value === undefined) return null

  const { scenario } = resolveCartLine(draftFacts.value, {
    storefrontInStock: storefrontInStock.value,
    stockLine: draftStockLine.value ?? undefined,
    itemId: productStore.selectedBasketItemId ?? undefined,
    stockConsumingLineIds: stockConsumingIds.value,
    specialOrderLeadTime: productStore.selectedProduct?.c_specialOrderLeadTime ?? 0,
  })

  return buildCollectRow(scenario, {
    store: shopStore.myStore,
    isResized: draftFacts.value.isResized,
    standaloneError: true,
  })
})
</script>

<template>
  <div v-if="showCollect" class="quickview-lead-time" data-testid="quickview-lead-time">
    <div
      v-if="!shopStore.myStore"
      class="quickview-lead-time__row quickview-lead-time__row--error"
      data-testid="quickview-lead-time__select-store"
    >
      <svg-icon name="mhj:click-and-collect-lock" class="quickview-lead-time__icon" />
      <span>{{ stripTrailingPeriod(t('product_details_page.find_store.select_store_click_and_collect')) }}</span>
    </div>
    <div
      v-else-if="collectRow"
      class="quickview-lead-time__row"
      :class="{ 'quickview-lead-time__row--error': collectRow.isError }"
      data-testid="quickview-lead-time__collect"
    >
      <svg-icon
        name="mhj:click-and-collect-lock"
        class="quickview-lead-time__icon"
        :data-testid="`quickview-lead-time__collect__status--${collectRow.isError ? 'unavailable' : 'available'}`"
      />
      <span data-testid="quickview-lead-time__collect__message">{{ collectRow.message }}</span>
    </div>
  </div>
  <ProductAvailability v-else :product-store-location="productStoreLocation" is-cart-style />
</template>

<style lang="postcss">
.quickview-lead-time {
  background: var(--surface-light-20);
  padding: var(--space-40);
  font-size: var(--text-small);

  .quickview-lead-time__row {
    display: inline-flex;
    gap: var(--space-20);
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    width: 100%;
  }

  .quickview-lead-time__icon {
    height: var(--space-50);
    flex-shrink: 0;
    color: var(--status-success);
  }

  /* Only the icon carries status colour. */
  .quickview-lead-time__row--error .quickview-lead-time__icon {
    color: var(--status-error);
  }
}
</style>
